import React, { Suspense, useRef } from 'react';

import { ShareConversationModalLazy } from './ShareConversationModalLazy';
import type { ShareConversationModalState } from './useShareConversationModalState';

/**
 * Bridge between the local UI state held by `useShareConversationModalState`
 * and the presentational `ShareConversationModal`.
 *
 * Kept deliberately thin: it owns no state of its own. It forwards the
 * tenant `cloudId` (and optional `productKey` / `principalId`) through to
 * the modal so that the embedded `SmartUserPicker` can search the org's
 * people directory directly via the recommendation service.
 */
export type ShareConversationModalContainerProps = {
	state: ShareConversationModalState;
	organizationName?: string;
	/** Cloud ID of the current tenant; forwarded to `SmartUserPicker`. */
	cloudId: string;
	/** Optional product key for `SmartUserPicker`; defaults to `"people"`. */
	productKey?: string;
	/** Optional AAID of the current user; improves suggestion ranking. */
	principalId?: string;
};

export const ShareConversationModalContainer = ({
	state,
	organizationName,
	cloudId,
	productKey,
	principalId,
}: ShareConversationModalContainerProps): JSX.Element | null => {
	// Defer mounting the lazy boundary (and therefore the chunk
	// download) until the user actually opens the modal for the first
	// time. Once it has been opened at least once we keep the boundary
	// mounted so the modal's own `<ModalTransition>` exit animation
	// runs on close — and so subsequent opens are fully synchronous
	// against the already-loaded chunk.
	const hasEverOpenedRef = useRef(false);
	if (state.isOpen) {
		hasEverOpenedRef.current = true;
	}
	if (!hasEverOpenedRef.current) {
		return null;
	}

	// `Suspense` fallback is `null` because the modal is invisible
	// until open anyway — there is nothing for the user to perceive
	// during the one-shot chunk fetch on first open.
	return (
		<Suspense fallback={null}>
			<ShareConversationModalLazy
				isOpen={state.isOpen}
				onClose={state.close}
				organizationName={organizationName}
				cloudId={cloudId}
				productKey={productKey}
				principalId={principalId}
				isSubmitting={state.isSubmitting}
				isLoadingInitialState={state.isLoadingInitialState}
				errorMessage={state.errorMessage}
				errorCode={state.errorCode}
				onSubmit={state.handleCopyLink}
				canGenerate={state.canGenerate}
				seeWhatOthersCanSeeHref={state.seeWhatOthersCanSeeHref}
				shareUrl={state.shareUrl}
				copyLinkCreatesShare
				autoSaveShareAccess={state.autoSaveShareAccess}
				isSavingShareAccess={state.isSavingShareAccess}
				onShareAccessChange={state.handleShareAccessChange}
				onBeforeCopyLink={state.flushShareAccessChanges}
				onCopyLinkSuccess={state.handleCopyLinkSuccess}
				onCopyLinkFailure={state.handleCopyLinkFailure}
				initialShareType={state.initialShareType}
				initialSelectedUsers={state.initialSelectedUsers}
				initialOwnerUser={state.initialOwnerUser}
				allowEmptySpecificUsers
				showSpecificPeopleAccessList
			/>
		</Suspense>
	);
};
